import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, FileUp, Loader2, MonitorPlay, Upload } from "lucide-react";
import { apiFetch } from "@/lib/api-client";
import {
  useSlides,
  type WebinarSlideAsset,
  type WebinarSlideCurrentState,
} from "@/hooks/use-webinar-control-room";

interface SlidesPanelProps {
  webinarId: string | undefined;
  compact?: boolean;
}

interface PendingSlideDeck {
  title: string;
  slides: WebinarSlideAsset[];
  currentIndex: number;
}

const ACCEPTED_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

function decksMatch(serverDeck: PendingSlideDeck | null | undefined, pendingDeck: PendingSlideDeck) {
  return Boolean(
    serverDeck
    && serverDeck.title === pendingDeck.title
    && serverDeck.currentIndex === pendingDeck.currentIndex
    && serverDeck.slides.length === pendingDeck.slides.length
    && serverDeck.slides.every((slide, index) => slide.id === pendingDeck.slides[index]?.id),
  );
}

function getUploadDisabledReason(webinarId: string | undefined, isDeckBusy: boolean) {
  if (!webinarId) {
    return "Slide controls stay disabled until webinar context loads.";
  }

  if (isDeckBusy) {
    return "Wait for the current upload or deck save to finish.";
  }

  return null;
}

function getSlideNavigationDisabledReason(
  direction: "previous" | "next",
  currentIndex: number,
  slideCount: number,
  isChangingSlide: boolean,
  isDeckBusy: boolean,
) {
  if (isChangingSlide) {
    return "Wait for the current live-slide change to finish.";
  }

  if (isDeckBusy) {
    return "Wait for the current upload or deck save to finish.";
  }

  if (direction === "previous" && currentIndex <= 0) {
    return "Already on the first live slide.";
  }

  if (direction === "next" && currentIndex >= slideCount - 1) {
    return "Already on the last live slide.";
  }

  return null;
}

const SLIDES_DISABLED_CONTROL_SURFACES = [
  {
    key: "lower-thirds",
    label: "Lower thirds",
    reason: "No compositor-backed lower-third live-output seam is mounted in this slides workflow.",
  },
  {
    key: "agenda-countdown",
    label: "Agenda and countdown",
    reason: "No timed rundown seam is mounted here, so agenda/countdown controls stay disabled.",
  },
  {
    key: "resource-links",
    label: "Resource links",
    reason: "No attendee-side live link injection seam is mounted from this slides workflow.",
  },
  {
    key: "breakout-timers",
    label: "Breakout timers",
    reason: "No breakout orchestration seam is mounted inside this slides workflow; hosts use the mounted webinar breakout management route.",
  },
] as const;

export function SlidesPanel({ webinarId, compact = false }: SlidesPanelProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { data: deck, refetch } = useSlides(webinarId);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isChangingSlide, setIsChangingSlide] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDeck, setPendingDeck] = useState<PendingSlideDeck | null>(null);

  // Browser-proof lineage: 60a5a47d-50e7-4a2b-b106-55cbb11a3509-startup-baseline-2026-04-20-execution-wave-1
  // Generated work: 60a5a47d-50e7-4a2b-b106-55cbb11a3509-startup-baseline-2026-04-20-execution-wave-1:WEB-100-006-presenter-production-tools
  // Keep the live deck UI aligned with the mutation lifecycle so upload/save/current-slide proof reflects the real control-room state.
  const resolvedDeck = pendingDeck ?? deck ?? null;
  const slides = resolvedDeck?.slides ?? [];
  const currentIndex = resolvedDeck?.currentIndex ?? 0;
  const currentSlide = slides[currentIndex] ?? null;
  const panelHeightClass = compact ? "max-h-[32rem]" : "h-full";
  const isDeckBusy = isUploading || isSaving;
  const uploadDisabledReason = getUploadDisabledReason(webinarId, isDeckBusy);
  const previousSlideDisabledReason = getSlideNavigationDisabledReason("previous", currentIndex, slides.length, isChangingSlide, isDeckBusy);
  const nextSlideDisabledReason = getSlideNavigationDisabledReason("next", currentIndex, slides.length, isChangingSlide, isDeckBusy);
  const visibleNavigationDisabledReason = previousSlideDisabledReason ?? nextSlideDisabledReason;
  const liveWorkflowStatus = error
    ? "Fix the current slide workflow error before continuing."
    : isUploading
      ? "Uploading slide assets..."
      : isSaving
        ? "Saving slide deck..."
        : isChangingSlide
          ? "Updating live slide..."
          : slides.length > 0
            ? "Ready for live slide switching."
            : "Upload a deck to enable live slide switching.";

  useEffect(() => {
    setPendingDeck(null);
  }, [webinarId]);

  useEffect(() => {
    if (!pendingDeck || !decksMatch(deck, pendingDeck)) {
      return;
    }

    // Packet 60a5a47d-50e7-4a2b-b106-55cbb11a3509-startup-baseline-2026-04-20-execution-wave-1:
    // keep optimistic slide state mounted until the route-backed deck refresh proves persistence.
    setPendingDeck(null);
  }, [deck, pendingDeck]);

  const deckTitle = useMemo(() => {
    if (resolvedDeck?.title) {
      return resolvedDeck.title;
    }
    return "Untitled deck";
  }, [resolvedDeck?.title]);

  async function saveSlideDeck(nextDeck: PendingSlideDeck) {
    if (!webinarId) {
      throw new Error("Missing webinar context");
    }

    await apiFetch<{ data: WebinarSlideCurrentState }>(`/v1/webinars/${webinarId}/slides`, {
      method: "PUT",
      body: JSON.stringify(nextDeck),
    });
  }

  async function pushCurrentSlide(index: number) {
    if (!webinarId) {
      throw new Error("Missing webinar context");
    }

    await apiFetch<{ data: WebinarSlideCurrentState }>(`/v1/webinars/${webinarId}/slides/current`, {
      method: "POST",
      body: JSON.stringify({ currentIndex: index }),
    });
  }

  async function uploadSlideAsset(file: File, name: string) {
    if (!webinarId) {
      throw new Error("Missing webinar context");
    }

    const formData = new FormData();
    formData.set("file", file);
    formData.set("name", name);

    const body = await apiFetch<{ data: WebinarSlideAsset }>(`/v1/webinars/${webinarId}/slides/upload`, {
      method: "POST",
      body: formData,
    });

    return body.data as WebinarSlideAsset;
  }

  async function convertPdfToImages(file: File) {
    const pdfjs = await import("pdfjs-dist");
    if (!pdfjs.GlobalWorkerOptions.workerSrc) {
      pdfjs.GlobalWorkerOptions.workerSrc = new URL(
        "pdfjs-dist/build/pdf.worker.mjs",
        import.meta.url,
      ).toString();
    }
    const arrayBuffer = await file.arrayBuffer();
    const pdfDocument = await pdfjs.getDocument(new Uint8Array(arrayBuffer)).promise;

    const convertedFiles: File[] = [];
    for (let pageIndex = 1; pageIndex <= pdfDocument.numPages; pageIndex += 1) {
      const page = await pdfDocument.getPage(pageIndex);
      const viewport = page.getViewport({ scale: 2 });
      const canvas = window.document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const context = canvas.getContext("2d");
      if (!context) {
        throw new Error("Unable to prepare slide canvas");
      }
      await page.render({ canvas, viewport, canvasContext: context }).promise;

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((value) => {
          if (value) {
            resolve(value);
            return;
          }
          reject(new Error("Failed to convert PDF page to image"));
        }, "image/jpeg", 0.92);
      });

      convertedFiles.push(
        new File(
          [blob],
          `${file.name.replace(/\.pdf$/i, "")}-page-${String(pageIndex).padStart(2, "0")}.jpg`,
          { type: "image/jpeg" },
        ),
      );
    }

    return convertedFiles;
  }

  async function handleFilesSelected(fileList: FileList | null) {
    if (!webinarId || !fileList || fileList.length === 0) {
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const nextAssets: WebinarSlideAsset[] = [...slides];
      for (const file of Array.from(fileList)) {
        const filesToUpload = file.type === "application/pdf"
          ? await convertPdfToImages(file)
          : [file];

        for (const assetFile of filesToUpload) {
          if (!ACCEPTED_IMAGE_TYPES.has(assetFile.type)) {
            throw new Error("Only JPEG, PNG, WebP, or PDF files are supported");
          }

          const uploaded = await uploadSlideAsset(assetFile, assetFile.name);
          nextAssets.push(uploaded);
        }
      }

      const nextTitle = resolvedDeck?.title || fileList[0]?.name.replace(/\.[a-z0-9]+$/i, "") || "Webinar slides";
      const nextDeck: PendingSlideDeck = {
        title: nextTitle,
        slides: nextAssets,
        currentIndex: Math.min(resolvedDeck?.currentIndex ?? 0, nextAssets.length - 1),
      };
      setPendingDeck(nextDeck);
      setIsSaving(true);
      try {
        await saveSlideDeck(nextDeck);
        await refetch();
      } finally {
        setIsSaving(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    } catch (uploadError) {
      setPendingDeck(null);
      setError(uploadError instanceof Error ? uploadError.message : "Failed to upload slide deck");
    } finally {
      setIsUploading(false);
    }
  }

  async function handleGoToSlide(index: number) {
    if (!resolvedDeck || index < 0 || index >= slides.length) {
      return;
    }
    setError(null);
    setPendingDeck({
      title: resolvedDeck.title,
      slides,
      currentIndex: index,
    });
    setIsChangingSlide(true);
    try {
      await pushCurrentSlide(index);
      await refetch();
    } catch (slideError) {
      setPendingDeck(null);
      setError(slideError instanceof Error ? slideError.message : "Failed to change live slide");
    } finally {
      setIsChangingSlide(false);
    }
  }

  return (
    <div className={`flex flex-col overflow-hidden ${panelHeightClass}`}>
      <div className="flex items-center justify-between border-b border-[var(--color-border)] px-4 py-3">
        <div>
          <p className="text-sm font-semibold text-[var(--color-foreground)]">Slides</p>
          <p className="text-xs text-[var(--color-muted-foreground)]">
            Upload images or a PDF deck, then push live slide changes to attendees.
          </p>
        </div>
        <div className="flex max-w-60 flex-col items-end gap-1">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={!webinarId || isDeckBusy}
            data-testid="slides-upload-button"
            title={uploadDisabledReason ?? undefined}
            className="inline-flex items-center gap-2 rounded-xl bg-[var(--color-primary)] px-3 py-2 text-sm font-medium text-white hover:bg-[var(--color-primary)]/90 disabled:opacity-50"
          >
            {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Upload deck
          </button>
          {uploadDisabledReason ? (
            <p data-testid="slides-upload-disabled-reason" className="text-right text-xs text-[var(--color-muted-foreground)]">
              {uploadDisabledReason}
            </p>
          ) : null}
          <input
            ref={fileInputRef}
            type="file"
            data-testid="slides-upload-input"
            accept="image/jpeg,image/png,image/webp,application/pdf,.pdf"
            multiple
            hidden
            onChange={(event) => void handleFilesSelected(event.target.files)}
          />
        </div>
      </div>

      <div
        data-testid="slides-disabled-control-summary"
        className="mx-4 mt-4 rounded-2xl border border-[var(--color-warning)]/20 bg-[var(--color-warning)]/10 px-4 py-3"
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--color-warning)]">
          Adjacent live-output controls stay disabled
        </p>
        <p className="mt-1 text-xs leading-5 text-[var(--color-muted-foreground)]">
          Slide import, deck save, and current-slide switching are live here. Lower thirds, agenda/countdown,
          resource links stay explicitly disabled until their dedicated live-output seams are mounted. Breakout timers are managed from the mounted webinar breakout route, not this slides workflow.
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {SLIDES_DISABLED_CONTROL_SURFACES.map((surface) => (
            <div
              key={surface.key}
              data-testid={`slides-disabled-control-${surface.key}`}
              className="rounded-xl border border-[var(--color-warning)]/20 bg-[var(--color-card)]/70 px-3 py-2"
            >
              <p className="text-xs font-semibold text-[var(--color-foreground)]">{surface.label}</p>
              <p className="mt-1 text-xs leading-5 text-[var(--color-muted-foreground)]">{surface.reason}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid min-h-0 flex-1 gap-4 overflow-hidden p-4 lg:grid-cols-[minmax(0,2fr)_20rem]">
        <div className="min-h-0 overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)]">
          {currentSlide ? (
            <div className="flex h-full flex-col">
              <div className="border-b border-[var(--color-border)] px-4 py-3">
                <p
                  data-testid="slides-live-label"
                  className="text-xs uppercase tracking-[0.16em] text-[var(--color-muted-foreground)]"
                >
                  Live slide {currentIndex + 1} of {slides.length}
                </p>
                <p className="mt-1 text-sm font-medium text-[var(--color-foreground)]">{deckTitle}</p>
              </div>
              <div className="flex-1 overflow-auto bg-[var(--color-surface)] p-4">
                <img
                  src={currentSlide.url}
                  alt={currentSlide.name}
                  className="mx-auto max-h-full rounded-xl border border-[var(--color-border)] object-contain shadow-sm"
                />
              </div>
              <div className="flex flex-col gap-2 border-t border-[var(--color-border)] px-4 py-3">
                {visibleNavigationDisabledReason ? (
                  <p data-testid="slides-navigation-disabled-reason" className="text-xs text-[var(--color-muted-foreground)]">
                    {visibleNavigationDisabledReason}
                  </p>
                ) : null}
                <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => void handleGoToSlide(currentIndex - 1)}
                  disabled={currentIndex <= 0 || isChangingSlide || isDeckBusy}
                  data-testid="slides-prev-button"
                  title={previousSlideDisabledReason ?? undefined}
                  className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-foreground)] disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => void handleGoToSlide(currentIndex + 1)}
                  disabled={currentIndex >= slides.length - 1 || isChangingSlide || isDeckBusy}
                  data-testid="slides-next-button"
                  title={nextSlideDisabledReason ?? undefined}
                  className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
              <MonitorPlay className="h-10 w-10 text-[var(--color-muted-foreground)]" />
              <div>
                <p className="text-sm font-medium text-[var(--color-foreground)]">No slide deck uploaded yet</p>
                <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
                  Upload slide images or a PDF and this panel becomes the live presenter deck.
                </p>
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={!webinarId || isDeckBusy}
                data-testid="slides-empty-upload-button"
                title={uploadDisabledReason ?? undefined}
                className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-3 py-2 text-sm text-[var(--color-foreground)]"
              >
                <FileUp className="h-4 w-4" />
                Choose files
              </button>
              {uploadDisabledReason ? (
                <p data-testid="slides-empty-upload-disabled-reason" className="text-xs text-[var(--color-muted-foreground)]">
                  {uploadDisabledReason}
                </p>
              ) : null}
            </div>
          )}
        </div>

        <div className="min-h-0 overflow-y-auto rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)]">
          <div className="border-b border-[var(--color-border)] px-4 py-3">
            <p className="text-sm font-semibold text-[var(--color-foreground)]">Deck outline</p>
            <p className="text-xs text-[var(--color-muted-foreground)]">
              Choose the active slide directly or step through the deck live.
            </p>
          </div>
          <div className="space-y-2 p-3">
            {slides.length === 0 ? (
              <p className="rounded-xl border border-dashed border-[var(--color-border)] px-3 py-4 text-center text-xs text-[var(--color-muted-foreground)]">
                Uploaded slides appear here.
              </p>
            ) : (
              slides.map((slide, index) => (
                <button
                  key={slide.id}
                  type="button"
                  onClick={() => void handleGoToSlide(index)}
                  disabled={isChangingSlide || isDeckBusy}
                  data-testid={`slides-outline-row-${index}`}
                  className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2 text-left transition ${
                    index === currentIndex
                      ? "border-[var(--color-primary)] bg-[var(--color-primary-light)]"
                      : "border-[var(--color-border)] bg-[var(--color-surface)]"
                  }`}
                >
                  <img
                    src={slide.url}
                    alt={slide.name}
                    className="h-14 w-20 rounded-lg border border-[var(--color-border)] object-cover"
                  />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-[var(--color-foreground)]">
                      {index + 1}. {slide.name}
                    </p>
                    <p className="text-xs text-[var(--color-muted-foreground)]">
                      {(slide.sizeBytes / 1024).toFixed(0)} KB
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      {error ? (
        <div className="border-t border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
          {error}
        </div>
      ) : null}
      <div
        data-testid="slides-panel-live-status"
        className="border-t border-[var(--color-border)] px-4 py-2 text-xs text-[var(--color-muted-foreground)]"
      >
        {liveWorkflowStatus}
      </div>
    </div>
  );
}
